/**
 * PlaidLinkButton Component
 * A button that initiates the Plaid Link flow for connecting bank accounts
 */

import { useCallback } from 'react';
import { Button } from '@/components/ui';
import { usePlaidLink, type PlaidLinkError } from '@/hooks/usePlaidLink';
import { useOnConnectionCreated } from '@/hooks/useConnections';
import type { ExchangeTokenResponse } from '@/types';

export interface PlaidLinkButtonProps {
  /**
   * Connection ID for re-authentication (optional)
   */
  connectionId?: string;
  /**
   * @deprecated WP-CONN-1: ignored. The backend chooses the products and always
   * requests transactions + investments consent, so passing
   * ["transactions", "investments"] here changes nothing. Mirrors the same
   * deprecation on UsePlaidLinkOptions.products.
   */
  products?: string[];
  /**
   * Callback when connection is successfully created
   */
  onSuccess?: (response: ExchangeTokenResponse) => void;
  /**
   * Callback when user exits Plaid Link
   */
  onExit?: (error: PlaidLinkError | null) => void;
  /**
   * Button variant
   */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /**
   * Button size
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Custom button text
   */
  children?: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Disabled state
   */
  disabled?: boolean;
  /**
   * Custom left icon (defaults to BankIcon)
   */
  leftIcon?: React.ReactNode;
}

/**
 * Bank connection icon
 */
export const BankIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
    />
  </svg>
);

/**
 * Investment/chart icon for brokerage connections
 */
export const InvestmentIcon = () => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
    />
  </svg>
);

/**
 * PlaidLinkButton - Initiates Plaid Link flow for bank connections
 *
 * @example
 * ```tsx
 * <PlaidLinkButton
 *   onSuccess={(response) => {
 *     toast.success('Bank connected successfully!');
 *   }}
 * >
 *   Connect Bank
 * </PlaidLinkButton>
 * ```
 */
export default function PlaidLinkButton({
  connectionId,
  products,
  onSuccess,
  onExit,
  variant = 'primary',
  size = 'md',
  children,
  className,
  disabled = false,
  leftIcon,
}: PlaidLinkButtonProps) {
  const onConnectionCreated = useOnConnectionCreated();

  const handleSuccess = useCallback(
    (response: ExchangeTokenResponse) => {
      // Update cache
      onConnectionCreated(response);
      // Notify parent
      onSuccess?.(response);
    },
    [onConnectionCreated, onSuccess]
  );

  const { open, ready, isLoadingToken, isExchanging } = usePlaidLink({
    connectionId,
    products,
    onSuccess: handleSuccess,
    onExit,
  });

  const isLoading = isLoadingToken || isExchanging;
  const isDisabled = disabled || !ready || isLoading;

  const handleClick = useCallback(() => {
    if (ready && !isLoading) {
      open();
    }
  }, [ready, isLoading, open]);

  const getButtonText = () => {
    if (isLoadingToken) {
      return 'Initializing...';
    }
    if (isExchanging) {
      return 'Connecting...';
    }
    return children || (connectionId ? 'Reconnect Bank' : 'Add Bank or Credit Card');
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      disabled={isDisabled}
      isLoading={isLoading}
      leftIcon={!isLoading ? (leftIcon ?? <BankIcon />) : undefined}
      onClick={handleClick}
      /*
       * WCAG 2.5.3 Label in Name (Level A): the accessible name must contain
       * the visible label.
       *
       * This used to be set unconditionally, which broke that whenever a caller
       * supplied its own text - ConnectionsPage.tsx:52 renders
       * <PlaidLinkButton>Connect Account</PlaidLinkButton>, so the button READ
       * "Connect Account" while its accessible name was "Add bank or credit
       * card account". A speech-input user saying "click Connect Account" could
       * not activate it, and a screen-reader user heard a different label than
       * a sighted user saw.
       *
       * When the caller provides children, the content IS the accessible name
       * and no aria-label is needed. The default below is only for the built-in
       * text, which it legitimately expands ("... account").
       */
      aria-label={
        children
          ? undefined
          : connectionId
            ? 'Reconnect bank account'
            : 'Add bank or credit card account'
      }
    >
      {getButtonText()}
    </Button>
  );
}
